/**
 * Where the platform is open for business.
 *
 * Each region is a circle: a centre and a radius. Vendors inside an active one
 * are visible to customers; vendors positively outside every one are not — they
 * keep their whole console, they simply aren't discoverable yet.
 *
 * The screen is built around the one question an admin actually has before
 * launching a city: "how many vendors does this cover?" Preview answers it
 * before anything is saved, and every write reports what it changed.
 */
import { useState } from 'react';
import { Loader2, MapPin, Plus, Power, Trash2, Crosshair } from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmModal } from '@/features/vendors/components/reason-modal';
import { ApiError } from '@/lib/api';
import { CITY_PRESETS, type RecomputeTally, type ServiceRegion } from '@/features/service-regions/types';
import {
  use_check_point,
  use_create_region,
  use_delete_region,
  use_preview_coverage,
  use_service_regions,
  use_update_region,
} from '@/features/service-regions/hooks';

function tally_sentence(t: RecomputeTally): string {
  return `${t.serviceable} vendor${t.serviceable === 1 ? '' : 's'} visible · ${t.out_of_area} outside · ${t.location_unknown} unplaced`;
}

export function ServiceRegionPage() {
  const { data, isLoading, error } = use_service_regions();
  const regions: ServiceRegion[] = data?.items ?? [];
  const create_m = use_create_region();
  const update_m = use_update_region();
  const delete_m = use_delete_region();
  const preview_m = use_preview_coverage();
  const check_m = use_check_point();

  const [open, set_open] = useState(false);
  const [name, set_name] = useState('');
  const [lat, set_lat] = useState('');
  const [lng, set_lng] = useState('');
  const [radius, set_radius] = useState('25');
  const [pincodes, set_pincodes] = useState('');
  const [form_error, set_form_error] = useState<string | null>(null);
  const [banner, set_banner] = useState<string | null>(null);

  const [pending_delete, set_pending_delete] = useState<ServiceRegion | null>(null);
  const [check_lat, set_check_lat] = useState('');
  const [check_lng, set_check_lng] = useState('');

  const numbers = () => ({
    centre_lat: Number(lat),
    centre_lng: Number(lng),
    radius_km: Number(radius),
  });
  const numbers_valid = () =>
    lat.trim() !== '' && lng.trim() !== '' && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)) && Number(radius) > 0;

  function reset() {
    set_name('');
    set_lat('');
    set_lng('');
    set_radius('25');
    set_pincodes('');
    set_form_error(null);
    preview_m.reset();
  }

  function submit() {
    set_form_error(null);
    if (!name.trim()) return set_form_error('Give the region a name — vendors see it.');
    if (!numbers_valid()) return set_form_error('A centre and a radius are required.');

    create_m.mutate(
      {
        name: name.trim(),
        ...numbers(),
        pincodes: pincodes
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean),
      },
      {
        onSuccess: (res) => {
          set_banner(`“${res.region.name}” is live — ${tally_sentence(res.tally)}.`);
          reset();
          set_open(false);
        },
        onError: (err) =>
          set_form_error(err instanceof ApiError ? err.message : 'Could not save this region.'),
      },
    );
  }

  function toggle(region: ServiceRegion) {
    update_m.mutate(
      { id: region.id, is_active: !region.is_active },
      {
        onSuccess: (res) =>
          set_banner(
            `“${res.region.name}” ${res.region.is_active ? 'switched on' : 'paused'} — ${tally_sentence(res.tally)}.`,
          ),
      },
    );
  }

  return (
    <DashboardLayout title="Service regions">
      <div className="flex flex-col gap-4">
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="min-w-0">
              <p className="font-heading text-lg font-bold">Where we operate</p>
              <p className="text-sm text-muted-foreground">
                Vendors inside a live region are visible to customers. Outside, they keep their
                account and everything in it — customers just can’t see them yet.
                {data && !data.gating_enabled && ' No regions yet, so the platform is open everywhere.'}
              </p>
              {/*
                The platform-wide split, on load.

                Per-region counts answer "how big is this area". They do not answer the question
                anyone actually has before pausing a region or opening the next city: how many
                vendors are dark right now. That number used to live only in the toast after a
                write — visible for a few seconds, to one person.
              */}
              {data?.gating_enabled && (
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="region-tally">
                  <span className="font-medium text-success" data-testid="tally-serviceable">
                    {data.tally.serviceable} visible to customers
                  </span>
                  <span className="font-medium text-destructive" data-testid="tally-out">
                    {data.tally.out_of_area} outside every live region
                  </span>
                  <span className="text-muted-foreground" data-testid="tally-unplaced">
                    {data.tally.location_unknown} unplaced
                    {/* Not a failure state: an unplaced vendor stays VISIBLE, so a missing
                        coordinate can never silently delete someone's shop. */}
                    <span className="ml-1 text-xs">(no address — still visible)</span>
                  </span>
                </div>
              )}
              {/*
                All paused is not the same as none drawn, and both look like an empty live list.
                One means the platform is open everywhere; the other means it is shut.
              */}
              {data?.gating_enabled && (regions ?? []).every((r) => !r.is_active) && (
                <p className="mt-2 text-sm font-medium text-destructive" data-testid="all-paused-warning">
                  Every region is paused, so customers can see nobody. Deleting the last region is
                  what reopens the platform — pausing it does not.
                </p>
              )}
            </div>
            <Button onClick={() => set_open(true)} data-testid="add-region">
              <Plus className="h-4 w-4" /> Add region
            </Button>
          </CardContent>
        </Card>

        {banner && (
          <Card className="border-success/40 bg-success/10">
            <CardContent className="flex items-center justify-between gap-3 p-3">
              <p className="text-sm font-semibold" data-testid="region-banner">
                {banner}
              </p>
              <button type="button" onClick={() => set_banner(null)} className="text-xs underline">
                Dismiss
              </button>
            </CardContent>
          </Card>
        )}

        {/* Create form */}
        {open && (
          <Card data-testid="region-form">
            <CardContent className="flex flex-col gap-3 p-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="region-name">Region name</Label>
                <Input
                  id="region-name"
                  value={name}
                  onChange={(e) => set_name(e.target.value)}
                  placeholder="Bengaluru — Central"
                  data-testid="region-name"
                />
              </div>

              <div className="flex flex-wrap gap-1.5">
                {CITY_PRESETS.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    data-testid={`preset-${c.label}`}
                    onClick={() => {
                      set_lat(String(c.lat));
                      set_lng(String(c.lng));
                      if (!name.trim()) set_name(c.label);
                    }}
                    className="rounded-full border-2 border-brand-outline-variant/25 px-2.5 py-1 text-xs font-semibold hover:border-primary/40"
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="region-lat">Centre latitude</Label>
                  <Input id="region-lat" value={lat} onChange={(e) => set_lat(e.target.value)} data-testid="region-lat" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="region-lng">Centre longitude</Label>
                  <Input id="region-lng" value={lng} onChange={(e) => set_lng(e.target.value)} data-testid="region-lng" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="region-radius">Radius (km)</Label>
                  <Input
                    id="region-radius"
                    value={radius}
                    onChange={(e) => set_radius(e.target.value)}
                    data-testid="region-radius"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="region-pincodes">Pincodes (optional, comma separated)</Label>
                <Input
                  id="region-pincodes"
                  value={pincodes}
                  onChange={(e) => set_pincodes(e.target.value)}
                  placeholder="560001, 560002"
                  data-testid="region-pincodes"
                />
                <p className="text-xs text-muted-foreground">
                  Also match these exactly — useful for vendors with an address but no map pin.
                </p>
              </div>

              {/* The question an admin actually has, answered before saving. */}
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  disabled={!numbers_valid() || preview_m.isPending}
                  onClick={() => preview_m.mutate(numbers())}
                  data-testid="preview-coverage"
                >
                  {preview_m.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
                  What would this cover?
                </Button>
                {preview_m.data && (
                  <p className="text-sm font-semibold" data-testid="preview-result">
                    {preview_m.data.vendors_covered} of {preview_m.data.vendors_total} vendors
                    {preview_m.data.sample.length > 0 &&
                      ` — e.g. ${preview_m.data.sample
                        .slice(0, 3)
                        .map((v) => v.business_name ?? 'Unnamed')
                        .join(', ')}`}
                  </p>
                )}
              </div>

              {form_error && (
                <p className="text-sm font-semibold text-destructive" data-testid="region-form-error">
                  {form_error}
                </p>
              )}

              <div className="flex gap-2">
                <Button onClick={submit} disabled={create_m.isPending} data-testid="save-region">
                  {create_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Save region
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    reset();
                    set_open(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Spot check */}
        <Card>
          <CardContent className="flex flex-wrap items-end gap-3 p-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="check-lat">Test a location</Label>
              <div className="flex gap-2">
                <Input
                  id="check-lat"
                  value={check_lat}
                  onChange={(e) => set_check_lat(e.target.value)}
                  placeholder="latitude"
                  className="w-36"
                  data-testid="check-lat"
                />
                <Input
                  value={check_lng}
                  onChange={(e) => set_check_lng(e.target.value)}
                  placeholder="longitude"
                  className="w-36"
                  data-testid="check-lng"
                />
                <Button
                  variant="outline"
                  disabled={!check_lat.trim() || !check_lng.trim() || check_m.isPending}
                  onClick={() => check_m.mutate({ lat: Number(check_lat), lng: Number(check_lng) })}
                  data-testid="run-check"
                >
                  Check
                </Button>
              </div>
            </div>
            {check_m.data && (
              <p className="text-sm font-semibold" data-testid="check-result">
                {check_m.data.status === 'SERVICEABLE'
                  ? `Served${check_m.data.region ? ` by ${check_m.data.region.name}` : ''}`
                  : check_m.data.status === 'OUT_OF_AREA'
                    ? 'Not served — a customer here sees nothing'
                    : 'No location given'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* The regions */}
        {isLoading && (
          <div className="flex justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        {error && <p className="text-sm text-destructive">Could not load regions.</p>}

        {regions && regions.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="regions-empty">
              No regions yet — the platform is open everywhere. Add one to start restricting where
              customers can see vendors.
            </CardContent>
          </Card>
        )}

        <div className="flex flex-col gap-3">
          {(regions ?? []).map((r) => (
            <Card key={r.id} data-testid={`region-${r.id}`}>
              <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 font-heading font-bold">
                    <MapPin className="h-4 w-4 text-primary" />
                    {r.name}
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        r.is_active ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'
                      }`}
                      data-testid={`region-state-${r.id}`}
                    >
                      {r.is_active ? 'Live' : 'Paused'}
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {r.radius_km} km around {r.centre_lat.toFixed(4)}, {r.centre_lng.toFixed(4)}
                    {r.pincodes.length > 0 && ` · ${r.pincodes.length} pincode(s)`}
                  </p>
                  <p className="text-sm font-semibold" data-testid={`region-count-${r.id}`}>
                    {r.vendor_count} vendor{r.vendor_count === 1 ? '' : 's'} in this region
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => toggle(r)}
                    disabled={update_m.isPending}
                    data-testid={`toggle-region-${r.id}`}
                  >
                    <Power className="h-4 w-4" /> {r.is_active ? 'Pause' : 'Switch on'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => set_pending_delete(r)}
                    data-testid={`delete-region-${r.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <ConfirmModal
        open={!!pending_delete}
        title={`Delete “${pending_delete?.name}”?`}
        description="Vendors matched to it will be re-judged against the remaining regions. Nothing about their accounts changes."
        confirm_label="Delete region"
        loading={delete_m.isPending}
        on_cancel={() => set_pending_delete(null)}
        on_confirm={() => {
          if (!pending_delete) return;
          delete_m.mutate(pending_delete.id, {
            onSuccess: () => {
              set_banner(`“${pending_delete.name}” deleted.`);
              set_pending_delete(null);
            },
          });
        }}
      />
    </DashboardLayout>
  );
}
